/**
 * Cliente del API PÚBLICO — sin credenciales, con polling guiado por el server.
 *
 * ## La mitad cliente de ADR-020
 *
 * El API sirve documentos cacheados con `ETag` + `Cache-Control: public,
 * max-age=N` y les mete `pollAfterMs` en el cuerpo. Este cliente hace lo único
 * que el server no puede hacer solo: **respetar ese ritmo**.
 *
 *  - `pollAfterMs` lo decide el SERVER, no el portal. Un sábado con tres
 *    partidos en simultáneo, la federación puede aflojar el ritmo desde el
 *    backend sin redeployar el portal ni pedirle nada a nadie. Un intervalo
 *    cableado acá sería exactamente el número que después no se puede tocar.
 *  - **La pestaña oculta no pollea.** Un portal público queda abierto en
 *    segundo plano durante horas; seguir pidiendo cada 5 s es gastar batería
 *    del que mira y ancho de banda del que sirve, para pintar una pantalla que
 *    nadie ve. Al volver a la pestaña se refresca de una.
 *  - **El `fetch` normal alcanza.** Con `Cache-Control` y `ETag` puestos, el
 *    browser revalida solo: dentro del `max-age` ni sale a la red, y después
 *    manda `If-None-Match` y convierte el 304 en un 200 desde su caché. No hay
 *    que reimplementar HTTP. Igual se maneja el 304 explícito, porque un
 *    `fetch` inyectado en los tests —o un proxy raro— sí lo puede devolver.
 *
 * ## Sin credenciales, a propósito
 *
 * Este cliente NUNCA manda `Authorization`. No es un descuido: si el portal
 * público mandara el token de la sesión abierta en esa misma máquina, el día
 * que alguien agregue un endpoint mal declarado el portal público estaría
 * pidiendo datos privados con la credencial de un admin. Son dos clientes
 * distintos (`ApiClient` y éste) para que esa confusión no sea posible.
 */

// ---------------------------------------------------------------------------
// Formas de respuesta (copiadas de apps/api/src/public/*)
// ---------------------------------------------------------------------------
//
// Se declaran acá copiando la forma, igual que `SeasonReport` y `Clip`: el
// portal consume JSON y no importa los módulos del API. La paridad la cuida
// `apps/portal/test/public-client.test.ts` contra las respuestas reales.

export interface PublicOrgBrand {
  name: string;
  shortName: string | null;
  slug: string;
  brandColor: string | null;
  locale: string;
}

export interface PublicTeamRef {
  teamId: string;
  name: string;
}

export interface PublicResultLine {
  setsHome: number;
  setsAway: number;
  sets: Array<{ home: number; away: number }>;
  pointsHome: number;
  pointsAway: number;
}

export type PublicFixtureStatus =
  | 'scheduled'
  | 'live'
  | 'played'
  | 'walkover'
  | 'postponed'
  | 'cancelled';

export interface PublicFixtureEntry {
  id: string;
  competitionId: string;
  competitionName: string;
  phaseId: string;
  phaseName: string;
  groupId: string | null;
  groupName: string | null;
  matchday: number;
  leg: number;
  round: { round: number; totalRounds: number; thirdPlace: boolean } | null;
  home: PublicTeamRef | null;
  away: PublicTeamRef | null;
  scheduledAt: string | null;
  venue: string | null;
  status: PublicFixtureStatus;
  result: PublicResultLine | null;
  matchId: string | null;
}

export interface PublicCompetitionSummary {
  id: string;
  name: string;
  season: string;
  status: 'draft' | 'active' | 'finished';
  matches: number;
  teams: number;
}

/** Una fila de la tabla. Misma forma que `StandingsRow` de `@scout/core`. */
export interface PublicStandingsRow {
  position: number;
  teamId: string;
  name: string;
  played: number;
  won: number;
  lost: number;
  setsWon: number;
  setsLost: number;
  setRatio: number | null;
  setDiff: number;
  pointsScored: number;
  pointsAgainst: number;
  pointRatio: number | null;
  pointDiff: number;
  points: number;
  adjustment: number;
  tiebreak: string | null;
  tied: boolean;
}

export interface PublicStandingsBlock {
  phaseId: string;
  phaseName: string;
  kind: 'regular' | 'groups' | 'playoff';
  groupId?: string;
  groupName?: string;
  rows: PublicStandingsRow[];
  matchesCounted: number;
  matchesPending: number;
}

export interface PublicCompetitionDetail {
  id: string;
  name: string;
  season: string;
  description: string | null;
  status: 'draft' | 'active' | 'finished';
  phases: Array<{ id: string; name: string; kind: string; groups: Array<{ id: string; name: string }> }>;
  teams: PublicTeamRef[];
  fixture: PublicFixtureEntry[];
  standings: PublicStandingsBlock[];
}

export type PublicMatchStatus = 'scheduled' | 'live' | 'finished';

export interface PublicSetLine {
  set: number;
  home: number;
  away: number;
  wonBy: 'home' | 'away' | null;
  live: boolean;
}

export interface PublicScorePoint {
  set: number;
  home: number;
  away: number;
  scorer: 'home' | 'away';
}

export interface PublicTeamLine {
  name: string;
  setsWon: number;
  points: number;
  pointsByReason: Record<string, number>;
}

export interface PublicMatchDetail {
  matchId: string;
  name: string;
  competition: string | null;
  venue: string | null;
  status: PublicMatchStatus;
  home: PublicTeamLine;
  away: PublicTeamLine;
  sets: PublicSetLine[];
  current: { set: number; home: number; away: number; serving: 'home' | 'away' | null } | null;
  progression: PublicScorePoint[];
  lastSeq: number;
}

/** El tablero del overlay: lo que viaja por el SSE del widget. */
export interface PublicScoreboard {
  matchId: string;
  status: PublicMatchStatus;
  home: { name: string; setsWon: number; score: number };
  away: { name: string; setsWon: number; score: number };
  set: number;
  serving: 'home' | 'away' | null;
  sets: PublicSetLine[];
  lastSeq: number;
}

/** Campos que el server le agrega a TODO documento público. */
interface Snapshot {
  generatedAt: string;
  /** Cada cuánto conviene volver a preguntar. Lo decide el server. */
  pollAfterMs: number;
}

export interface PublicHomeResponse extends Snapshot {
  org: PublicOrgBrand;
  competitions: PublicCompetitionSummary[];
  live: PublicFixtureEntry[];
  upcoming: PublicFixtureEntry[];
  recent: PublicFixtureEntry[];
}

export interface PublicResultsResponse extends Snapshot {
  org: PublicOrgBrand;
  competitions: PublicCompetitionSummary[];
  entries: PublicFixtureEntry[];
  truncated: boolean;
}

export interface PublicCompetitionResponse extends Snapshot {
  org: PublicOrgBrand;
  competition: PublicCompetitionDetail | null;
}

export interface PublicMatchResponse extends Snapshot {
  org: PublicOrgBrand;
  fixture: PublicFixtureEntry;
  match: PublicMatchDetail | null;
}

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------

export type PublicErrorKind = 'network' | 'not_found' | 'server' | 'unknown';

export class PublicApiError extends Error {
  constructor(
    public readonly kind: PublicErrorKind,
    message: string,
    public readonly status = 0,
  ) {
    super(message);
    this.name = 'PublicApiError';
  }
}

// ---------------------------------------------------------------------------
// Cliente
// ---------------------------------------------------------------------------

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface PublicClientOptions {
  /** Base del API; vacío = mismo origen que el portal (el caso del despliegue). */
  baseUrl?: string;
  fetchImpl?: FetchLike;
}

/** Fallback cuando el server no dijo nada (no debería pasar: siempre lo manda). */
export const DEFAULT_POLL_MS = 15_000;

export class PublicClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: FetchLike;
  /**
   * Último documento por URL. Sirve para dos cosas: contestar un `304` sin
   * cuerpo, y —lo que más se nota en pantalla— seguir mostrando el marcador
   * viejo mientras se recarga, en vez de parpadear a "cargando" cada 5 s.
   */
  private readonly last = new Map<string, { etag: string | null; body: unknown }>();

  constructor(options: PublicClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.fetchImpl =
      options.fetchImpl ??
      ((input, init) => {
        const g = globalThis as { fetch?: FetchLike };
        if (!g.fetch) {
          return Promise.reject(new PublicApiError('network', 'Este navegador no tiene fetch'));
        }
        return g.fetch(input, init);
      });
  }

  url(path: string): string {
    const base = this.baseUrl.replace(/\/+$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private async get<T>(path: string): Promise<T> {
    const url = this.url(path);
    const previous = this.last.get(url);
    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        headers: {
          accept: 'application/json',
          ...(previous?.etag ? { 'if-none-match': previous.etag } : {}),
        },
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new PublicApiError('network', reason);
    }

    // 304: el documento no cambió. Con `fetch` del browser esto casi nunca
    // llega (lo resuelve la caché HTTP y devuelve 200), pero un proxy o un
    // `fetch` inyectado sí lo pueden devolver.
    if (response.status === 304 && previous) return previous.body as T;

    if (response.status === 404) {
      throw new PublicApiError('not_found', 'No encontrado', 404);
    }
    if (!response.ok) {
      throw new PublicApiError(
        response.status >= 500 ? 'server' : 'unknown',
        `HTTP ${response.status}`,
        response.status,
      );
    }

    const body = (await response.json()) as T;
    this.last.set(url, { etag: response.headers.get('etag'), body });
    return body;
  }

  /**
   * Ficha de la organización: nombre, slug y marca. Es lo que pinta el
   * encabezado del portal.
   *
   * Va por la ruta de la Etapa 1 (`/api/public/orgs/:slug`, la más chica que
   * hay) y **no se pollea**: el nombre y el color de una federación no cambian
   * durante un partido. Sacarla del documento de la portada habría hecho que
   * el marco de la página dependiera de una pantalla concreta y que la portada
   * se pidiera dos veces.
   */
  async getOrg(slug: string): Promise<PublicOrgBrand> {
    const org = await this.get<{
      name: string;
      slug: string;
      config: { shortName?: string; brandColor?: string; locale?: string };
    }>(`/api/public/orgs/${encodeURIComponent(slug)}`);
    return {
      name: org.name,
      shortName: org.config.shortName ?? null,
      slug: org.slug,
      brandColor: org.config.brandColor ?? null,
      locale: org.config.locale ?? 'es',
    };
  }

  getHome(slug: string): Promise<PublicHomeResponse> {
    return this.get<PublicHomeResponse>(`/api/public/orgs/${encodeURIComponent(slug)}/home`);
  }

  getResults(slug: string): Promise<PublicResultsResponse> {
    return this.get<PublicResultsResponse>(`/api/public/orgs/${encodeURIComponent(slug)}/results`);
  }

  getCompetition(slug: string, competitionId: string): Promise<PublicCompetitionResponse> {
    return this.get<PublicCompetitionResponse>(
      `/api/public/orgs/${encodeURIComponent(slug)}/competitions/${encodeURIComponent(competitionId)}`,
    );
  }

  getMatch(slug: string, matchId: string): Promise<PublicMatchResponse> {
    return this.get<PublicMatchResponse>(
      `/api/public/orgs/${encodeURIComponent(slug)}/matches/${encodeURIComponent(matchId)}`,
    );
  }

  /** Endpoint SSE del widget (lo consume `lib/live.ts`, sin headers de auth). */
  liveUrl(slug: string, matchId: string): string {
    return this.url(
      `/api/public/orgs/${encodeURIComponent(slug)}/matches/${encodeURIComponent(matchId)}/live`,
    );
  }
}

// ---------------------------------------------------------------------------
// Helpers de presentación (puros: viven acá para poder testearlos sin DOM)
// ---------------------------------------------------------------------------

/**
 * ¿Este partido es de "hoy" para EL QUE MIRA?
 *
 * El corte por día lo hace el cliente y no el server, porque el server no sabe
 * en qué huso horario está el espectador: un partido de las 21:00 en Salta es
 * del día siguiente en UTC. Además, si "hoy" dependiera de la zona del que
 * pregunta, habría una entrada de caché por huso horario y el snapshot dejaría
 * de servir para todos (ver ADR-020).
 */
export function isSameLocalDay(iso: string | null, reference: Date): boolean {
  if (iso === null) return false;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return false;
  return (
    at.getFullYear() === reference.getFullYear() &&
    at.getMonth() === reference.getMonth() &&
    at.getDate() === reference.getDate()
  );
}

/**
 * Los partidos del día, en el orden en que se leen: primero los que se están
 * jugando, después los que faltan y al final los que ya terminaron.
 */
export function matchesOfDay(
  home: Pick<PublicHomeResponse, 'live' | 'upcoming' | 'recent'>,
  reference: Date,
): PublicFixtureEntry[] {
  const orden: Record<string, number> = { live: 0, scheduled: 1 };
  const seen = new Set<string>();
  const out: PublicFixtureEntry[] = [];
  for (const entry of [...home.live, ...home.upcoming, ...home.recent]) {
    if (seen.has(entry.id)) continue;
    // Un partido en vivo entra aunque su horario declarado sea de ayer (empezó
    // tarde, o cruzó la medianoche): lo que se está jugando es de hoy.
    if (entry.status !== 'live' && !isSameLocalDay(entry.scheduledAt, reference)) continue;
    seen.add(entry.id);
    out.push(entry);
  }
  return out.sort((a, b) => {
    const pa = orden[a.status] ?? 2;
    const pb = orden[b.status] ?? 2;
    if (pa !== pb) return pa - pb;
    return (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? '');
  });
}

/** Sets ganados según el resultado del fixture, o del marcador si está en vivo. */
export function setsLabel(entry: PublicFixtureEntry): string {
  if (entry.result === null) return '–';
  return `${entry.result.setsHome} - ${entry.result.setsAway}`;
}
